import { api } from '../client';

import type { ListResponse, PaginationParams } from './common';
import {
  rememberResponseItemsUpdatedAt,
  rememberResponseUpdatedAt,
  type StockDocumentMutationDeps,
  type StockDocumentMutationOptions,
} from './documentMutationCache';

export interface StockIngredient {
  id: string;
  name: string;
  unit_code: string;
  is_weighted: boolean;
  category_id?: string | null;
  category_name?: string | null;
  category_kind?: 'raw' | 'intermediate' | null;
  default_cost?: string | null;
  is_active: boolean;
  recipes_count: number;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface IngredientListSummary {
  by_status: Record<string, number>;
}

export interface StockIngredientsResponse extends ListResponse<StockIngredient> {
  summary?: IngredientListSummary | null;
}

export interface StockIngredientDetailResponse {
  request_id: string;
  ingredient: StockIngredient;
}

// -- Detail sub-endpoint types ------------------------------------------------

export interface IngredientSupplier {
  supplier_id: string;
  supplier_name: string;
  supplier_short_name?: string | null;
  supplier_full_name?: string | null;
  supplier_product_name?: string | null;
  latest_unit_price?: string | null;
  latest_price_date?: string | null;
  first_purchase_date?: string | null;
  last_purchase_date?: string | null;
  purchase_count?: number;
  total_amount?: string | null;
  total_amount_gross?: string | null;
  avg_unit_price?: string | null;
}

export interface IngredientSuppliersResponse {
  request_id: string;
  items: IngredientSupplier[];
}

export interface IngredientSupplierSummary {
  supplier_id: string;
  supplier_name: string;
  supplier_short_name?: string | null;
  supplier_full_name?: string | null;
  latest_unit_price?: string | null;
  latest_price_date?: string | null;
  first_purchase_date?: string | null;
  last_purchase_date?: string | null;
  purchase_count?: number;
  total_amount?: string | null;
  total_amount_gross?: string | null;
  avg_unit_price?: string | null;
}

export interface IngredientPurchaseSummary {
  request_id: string;
  ingredient_id: string;
  suppliers: IngredientSupplierSummary[];
  price_stats: IngredientPriceStats;
}

export interface IngredientPurchaseHistoryItem {
  date: string;
  supplier_id: string;
  supplier_name: string;
  supplier_short_name?: string | null;
  supplier_product_name?: string | null;
  quantity: string;
  unit_code: string;
  unit_price: string;
  total_price: string;
  vat_rate?: string | null;
  receipt_id: string;
}

export interface IngredientPurchaseHistoryResponse {
  request_id: string;
  items: IngredientPurchaseHistoryItem[];
  paging: { limit: number; offset: number; total: number; has_more: boolean };
}

export interface IngredientPriceStats {
  request_id: string;
  current_price?: string | null;
  current_price_date?: string | null;
  min_price?: string | null;
  max_price?: string | null;
  avg_price?: string | null;
  period_days: number;
  data_points: number;
}

export interface IngredientUsedInRecipe {
  recipe_id: string;
  recipe_name: string;
  product_name?: string | null;
  product_category_name?: string | null;
  ingredient_quantity: string;
  ingredient_unit_code: string;
  recipe_yield_quantity?: string | null;
  recipe_yield_unit_code?: string | null;
  is_active: boolean;
}

export interface IngredientUsedInSummary {
  direct_count: number;
  intermediate_count: number;
  final_count: number;
  total_count: number;
}

export interface IngredientRecipeChain {
  final_recipe_id: string;
  final_recipe_name: string;
  final_product_name?: string | null;
  final_product_category_name?: string | null;
  final_product_ownership?: string | null;
  depth: number;
  path_recipe_ids: string[];
  path_recipe_names: string[];
  path_product_names: string[];
}

export interface IngredientUsedInRecipesResponse {
  request_id: string;
  items: IngredientUsedInRecipe[];
  direct_items?: IngredientUsedInRecipe[];
  intermediate_items?: IngredientUsedInRecipe[];
  final_items?: IngredientUsedInRecipe[];
  chains?: IngredientRecipeChain[];
  summary?: IngredientUsedInSummary;
}

export interface IngredientPurchaseHistoryFilters {
  supplier_id?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

export interface StockIngredientFilters extends PaginationParams {
  [key: string]: string | number | boolean | undefined;
  is_active?: boolean;
  category_id?: string;
  search?: string;
  supplier_receiptable_only?: boolean;
  include_produced_products?: boolean;
  include_summary?: boolean;
}

export interface CreateStockIngredientRequest {
  name: string;
  unit_code: string;
  category_id?: string | null;
  default_cost?: string | null;
  is_active?: boolean;
}

export interface UpdateStockIngredientRequest {
  name: string;
  unit_code: string;
  category_id: string | null;
  default_cost: string | null;
  is_active: boolean;
}

type IngredientStockEntity = 'ingredient';

export type StockIngredientsApiDeps = Pick<
  StockDocumentMutationDeps<IngredientStockEntity>,
  'rememberStockDocumentUpdatedAt' | 'withExpectedUpdatedAt'
>;

export const createStockIngredientsApi = ({
  rememberStockDocumentUpdatedAt,
  withExpectedUpdatedAt,
}: StockIngredientsApiDeps) => ({
  list: async (filters: StockIngredientFilters = {}): Promise<StockIngredientsResponse> => {
    return rememberResponseItemsUpdatedAt(
      rememberStockDocumentUpdatedAt,
      'ingredient',
      await api.webAdmin.get<StockIngredientsResponse>('/stock/ingredients', { params: filters }),
      (response) => response.items,
      (item) => item.id,
      (item) => item.updated_at,
    );
  },

  detail: async (id: string): Promise<StockIngredientDetailResponse> => {
    return rememberResponseUpdatedAt(
      rememberStockDocumentUpdatedAt,
      'ingredient',
      await api.webAdmin.get<StockIngredientDetailResponse>(`/stock/ingredients/${id}`),
      (response) => response.ingredient.id,
      (response) => response.ingredient.updated_at,
    );
  },

  create: async (
    data: CreateStockIngredientRequest,
    options?: StockDocumentMutationOptions,
  ): Promise<StockIngredientDetailResponse> => {
    const requestOptions = options?.idempotencyKey
      ? { idempotencyKey: options.idempotencyKey }
      : undefined;
    return rememberResponseUpdatedAt(
      rememberStockDocumentUpdatedAt,
      'ingredient',
      await api.webAdmin.post<StockIngredientDetailResponse>(
        '/stock/ingredients',
        data,
        requestOptions,
      ),
      (response) => response.ingredient.id,
      (response) => response.ingredient.updated_at,
    );
  },

  update: async (
    id: string,
    data: UpdateStockIngredientRequest,
    options?: StockDocumentMutationOptions,
  ): Promise<StockIngredientDetailResponse> => {
    return rememberResponseUpdatedAt(
      rememberStockDocumentUpdatedAt,
      'ingredient',
      await api.webAdmin.put<StockIngredientDetailResponse>(
        `/stock/ingredients/${id}`,
        data,
        withExpectedUpdatedAt('ingredient', id, options),
      ),
      (response) => response.ingredient.id,
      (response) => response.ingredient.updated_at,
    );
  },

  activate: async (
    id: string,
    options?: StockDocumentMutationOptions,
  ): Promise<StockIngredientDetailResponse> => {
    const requestOptions = options?.idempotencyKey
      ? { idempotencyKey: options.idempotencyKey }
      : undefined;
    return rememberResponseUpdatedAt(
      rememberStockDocumentUpdatedAt,
      'ingredient',
      await api.webAdmin.post<StockIngredientDetailResponse>(
        `/stock/ingredients/${id}/activate`,
        undefined,
        requestOptions,
      ),
      (response) => response.ingredient.id,
      (response) => response.ingredient.updated_at,
    );
  },

  deactivate: async (
    id: string,
    options?: StockDocumentMutationOptions,
  ): Promise<StockIngredientDetailResponse> => {
    const requestOptions = options?.idempotencyKey
      ? { idempotencyKey: options.idempotencyKey }
      : undefined;
    return rememberResponseUpdatedAt(
      rememberStockDocumentUpdatedAt,
      'ingredient',
      await api.webAdmin.post<StockIngredientDetailResponse>(
        `/stock/ingredients/${id}/deactivate`,
        undefined,
        requestOptions,
      ),
      (response) => response.ingredient.id,
      (response) => response.ingredient.updated_at,
    );
  },

  suppliers: async (id: string): Promise<IngredientSuppliersResponse> => {
    return api.webAdmin.get<IngredientSuppliersResponse>(`/stock/ingredients/${id}/suppliers`);
  },

  purchaseHistory: async (
    id: string,
    filters?: IngredientPurchaseHistoryFilters,
  ): Promise<IngredientPurchaseHistoryResponse> => {
    const params = filters
      ? Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== undefined))
      : undefined;
    return api.webAdmin.get<IngredientPurchaseHistoryResponse>(
      `/stock/ingredients/${id}/purchase-history`,
      params ? { params } : undefined,
    );
  },

  priceStats: async (id: string, options?: { days?: number }): Promise<IngredientPriceStats> => {
    const params = options?.days != null ? { days: options.days } : undefined;
    return api.webAdmin.get<IngredientPriceStats>(
      `/stock/ingredients/${id}/price-stats`,
      params ? { params } : undefined,
    );
  },

  purchaseSummary: async (
    id: string,
    options?: { days?: number; as_of_date?: string },
  ): Promise<IngredientPurchaseSummary> => {
    const params =
      options?.days != null || options?.as_of_date != null
        ? {
            days: options.days,
            as_of_date: options.as_of_date,
          }
        : undefined;
    return api.webAdmin.get<IngredientPurchaseSummary>(
      `/stock/ingredients/${id}/purchase-summary`,
      params ? { params } : undefined,
    );
  },

  usedInRecipes: async (id: string): Promise<IngredientUsedInRecipesResponse> => {
    return api.webAdmin.get<IngredientUsedInRecipesResponse>(
      `/stock/ingredients/${id}/used-in-recipes`,
    );
  },
});
